/**
 * Support Insights — PHOTON-29.
 *
 * Two tabs:
 *   - Assistant: RAG Q&A streamed from /api/freshdesk/ask (the implemented surface).
 *   - Insights: still a placeholder until the trend / spike detector lands.
 *
 * Assistant streaming model:
 *   - User types a question, presses Send (or Enter).
 *   - We open an SSE stream to the freshdesk-service. The first event back is
 *     `citations` with the top-K retrieved tickets; we render those right away
 *     so the user has something to look at while Claude is still composing.
 *   - `delta` events arrive as Claude streams, appending to the in-progress
 *     assistant bubble.
 *   - `usage` and `done` close out the message; errors surface via a MessageBar.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Button,
  Textarea,
  Spinner,
  Card,
  CardHeader,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
  Tab,
  TabList,
  TabValue,
  SelectTabEvent,
  SelectTabData,
  Badge,
} from "@fluentui/react-components";
import {
  Chat24Regular,
  DataTrending24Regular,
  Headset48Regular,
  Send24Regular,
  Dismiss24Regular,
  DocumentText24Regular,
  DatabaseSearch20Regular,
  Search20Regular,
  Document20Regular,
  CheckmarkCircle20Filled,
  ErrorCircle20Filled,
  Add20Regular,
  Delete16Regular,
  ChatMultiple20Regular,
} from "@fluentui/react-icons";

import { VoiceTextInputButton } from "../../components/VoiceTextInputButton";
import {
  useFreshdeskApiClient,
  FreshdeskCitation,
  FreshdeskToolCall,
  FreshdeskToolResult,
  FreshdeskUsage,
  FreshdeskThreadSummary,
} from "../../hooks/useFreshdeskApiClient";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------
const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  tabBody: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  // Assistant tab — two-pane: thread sidebar + chat
  assistantLayout: {
    flex: 1,
    display: "flex",
    flexDirection: "row",
    overflow: "hidden",
    minHeight: 0,
  },
  sidebar: {
    width: "260px",
    flexShrink: 0,
    display: "flex",
    flexDirection: "column",
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    overflow: "hidden",
  },
  sidebarHeader: {
    padding: tokens.spacingHorizontalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  newChatButton: {
    width: "100%",
    justifyContent: "flex-start",
  },
  threadList: {
    flex: 1,
    overflow: "auto",
    display: "flex",
    flexDirection: "column",
    padding: tokens.spacingHorizontalXS,
    gap: "2px",
  },
  threadItem: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalXS,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalS}`,
    borderRadius: tokens.borderRadiusMedium,
    cursor: "pointer",
    "&:hover": { backgroundColor: tokens.colorNeutralBackground2 },
    "&:hover .thread-delete": { opacity: 1 },
  },
  threadItemActive: {
    backgroundColor: tokens.colorNeutralBackground2Selected,
    "&:hover": { backgroundColor: tokens.colorNeutralBackground2Selected },
  },
  threadItemText: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
  },
  threadTitle: {
    fontSize: "13px",
    fontWeight: 500,
    color: tokens.colorNeutralForeground1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  threadPreview: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  threadDelete: {
    opacity: 0,
    flexShrink: 0,
    transition: "opacity 0.1s ease",
  },
  sidebarEmpty: {
    padding: tokens.spacingHorizontalM,
    color: tokens.colorNeutralForeground3,
    fontSize: "12px",
    textAlign: "center",
  },
  chatPane: {
    flex: 1,
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  // Assistant tab
  messages: {
    flex: 1,
    overflow: "auto",
    padding: tokens.spacingVerticalL,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalL,
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
    padding: tokens.spacingVerticalXXL,
  },
  emptySamples: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
    maxWidth: "640px",
  },
  sampleButton: {
    justifyContent: "flex-start",
    textAlign: "left",
    whiteSpace: "normal",
    height: "auto",
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
  },
  userBubble: {
    alignSelf: "flex-end",
    maxWidth: "75%",
    backgroundColor: "#004C97",
    color: "#ffffff",
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderRadius: "16px 16px 4px 16px",
    whiteSpace: "pre-wrap",
  },
  assistantBlock: {
    alignSelf: "flex-start",
    maxWidth: "92%",
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
  },
  assistantBubble: {
    backgroundColor: tokens.colorNeutralBackground1,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL}`,
    borderRadius: "16px 16px 16px 4px",
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    overflow: "hidden",  // contain wide pre blocks so the bubble itself stays bounded
    "& p:first-child": { marginTop: 0 },
    "& p:last-child": { marginBottom: 0 },
    "& code": {
      backgroundColor: tokens.colorNeutralBackground3,
      padding: "1px 4px",
      borderRadius: "3px",
      fontSize: "13px",
    },
    // <pre> from fenced code blocks: horizontal scroll instead of clipping
    "& pre": {
      backgroundColor: tokens.colorNeutralBackground3,
      padding: tokens.spacingHorizontalS,
      borderRadius: "6px",
      fontSize: "12px",
      lineHeight: "1.4",
      overflowX: "auto",
      maxWidth: "100%",
    },
    "& pre code": {
      backgroundColor: "transparent",
      padding: 0,
      fontSize: "12px",
      whiteSpace: "pre",
    },
    // Markdown tables (remark-gfm) — make them look like tables, not lists
    "& table": {
      borderCollapse: "collapse",
      marginTop: tokens.spacingVerticalS,
      marginBottom: tokens.spacingVerticalS,
      fontSize: "13px",
    },
    "& th, & td": {
      border: `1px solid ${tokens.colorNeutralStroke2}`,
      padding: "4px 8px",
      textAlign: "left",
    },
    "& th": {
      backgroundColor: tokens.colorNeutralBackground3,
      fontWeight: 600,
    },
  },
  citationsHeader: {
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalXS,
  },
  citationGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: tokens.spacingHorizontalS,
  },
  citationCard: {
    padding: tokens.spacingVerticalS,
    fontSize: "12px",
  },
  citationSubject: {
    fontSize: "13px",
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  citationMeta: {
    marginTop: tokens.spacingVerticalXS,
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground3,
  },
  toolList: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
  },
  toolHeader: {
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalXS,
  },
  toolCard: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXS,
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: "8px",
    padding: tokens.spacingHorizontalS,
    fontSize: "12px",
    cursor: "pointer",
    transition: "background-color 0.15s ease",
    "&:hover": { backgroundColor: tokens.colorNeutralBackground2 },
  },
  toolRow: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  toolName: {
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    fontFamily: "monospace",
    fontSize: "12px",
  },
  toolSummary: {
    marginLeft: "auto",
    color: tokens.colorNeutralForeground3,
    fontSize: "11px",
  },
  toolDetail: {
    marginTop: tokens.spacingVerticalXS,
    padding: tokens.spacingHorizontalXS,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: "4px",
    fontFamily: "monospace",
    fontSize: "11px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    color: tokens.colorNeutralForeground2,
    maxHeight: "240px",
    overflow: "auto",
  },
  toolError: { color: tokens.colorPaletteRedForeground1 },
  toolOk: { color: tokens.colorPaletteGreenForeground1 },
  inputRow: {
    padding: tokens.spacingVerticalL,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "flex-end",
  },
  textarea: { flex: 1 },
  errorBar: {
    margin: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalL} 0`,
  },
  usageRow: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    paddingLeft: tokens.spacingHorizontalM,
  },
  insightsPlaceholder: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    gap: tokens.spacingVerticalL,
    color: tokens.colorNeutralForeground2,
    textAlign: "center",
    padding: tokens.spacingVerticalXXL,
  },
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
interface ToolEvent {
  call: FreshdeskToolCall;
  result?: FreshdeskToolResult;
}

interface AssistantMessage {
  id: string;
  role: "user" | "assistant";
  text: string;
  citations?: FreshdeskCitation[];
  toolEvents: ToolEvent[];
  usage?: FreshdeskUsage;
  streaming?: boolean;
  error?: string;
}

// ---------------------------------------------------------------------------
// AssistantTab
// ---------------------------------------------------------------------------
const SAMPLE_QUESTIONS = [
  "What are the most common reasons customers ask for replacement body-worn cameras?",
  "How many tickets in the last 90 days mention docking station upload failures?",
  "What's the typical resolution for LensLocker Portal user-add requests?",
  "Are battery issues more common in Gen 12 or Gen 12.5 BWCs?",
];

const AssistantTab: React.FC = () => {
  const styles = useStyles();
  const client = useFreshdeskApiClient();
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState<AssistantMessage[]>([]);
  const [streaming, setStreaming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [threads, setThreads] = useState<FreshdeskThreadSummary[]>([]);
  const [threadId, setThreadId] = useState<string | null>(null);
  const [loadingThread, setLoadingThread] = useState(false);
  const cancelRef = useRef<(() => void) | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // useFreshdeskApiClient() returns a fresh object each render, so we read it
  // through a ref inside refreshThreads. That keeps refreshThreads stable
  // (empty deps) — otherwise the mount effect below would re-run every render
  // and hammer listThreads in an infinite loop.
  const clientRef = useRef(client);
  clientRef.current = client;

  const refreshThreads = useCallback(() => {
    if (!clientRef.current.isConfigured()) return;
    clientRef.current.listThreads().then(setThreads).catch(() => {/* sidebar best-effort */});
  }, []);

  // Load the sidebar once on mount.
  useEffect(() => {
    refreshThreads();
  }, [refreshThreads]);

  const scrollToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
      }
    });
  }, []);

  const submit = useCallback(
    async (questionRaw: string) => {
      const question = questionRaw.trim();
      if (!question || streaming) return;

      if (!client.isConfigured()) {
        setError("freshdeskServiceUrl is not set in config.json. Cannot reach the service.");
        return;
      }

      const userMsg: AssistantMessage = {
        id: `u_${Date.now()}`,
        role: "user",
        text: question,
        toolEvents: [],
      };
      const assistantId = `a_${Date.now()}`;
      const assistantPlaceholder: AssistantMessage = {
        id: assistantId,
        role: "assistant",
        text: "",
        toolEvents: [],
        streaming: true,
      };
      setMessages((m) => [...m, userMsg, assistantPlaceholder]);
      setInput("");
      setStreaming(true);
      setError(null);
      scrollToBottom();

      // Ensure a thread exists so this exchange is persisted and follow-ups
      // carry prior turns. Lazily created from the first question; if creation
      // fails we still answer, just statelessly for this turn.
      let tid = threadId;
      if (!tid) {
        try {
          const t = await client.createThread(question);
          tid = t.id;
          setThreadId(t.id);
          setThreads((prev) => [
            {
              id: t.id,
              title: t.title,
              message_count: 0,
              created_at: t.created_at,
              updated_at: t.updated_at,
              preview: question.slice(0, 100),
            },
            ...prev,
          ]);
        } catch {
          tid = null;
        }
      }

      const updateAssistant = (mut: (m: AssistantMessage) => AssistantMessage) => {
        setMessages((prev) =>
          prev.map((msg) => (msg.id === assistantId ? mut(msg) : msg)),
        );
      };

      cancelRef.current = client.streamAsk(
        { question, thread_id: tid ?? undefined },
        {
          onCitations: (hits) => {
            updateAssistant((m) => ({ ...m, citations: hits }));
            scrollToBottom();
          },
          onDelta: (text) => {
            updateAssistant((m) => ({ ...m, text: m.text + text }));
            scrollToBottom();
          },
          onToolCall: (call) => {
            updateAssistant((m) => ({
              ...m,
              toolEvents: [...m.toolEvents, { call }],
            }));
            scrollToBottom();
          },
          onToolResult: (result) => {
            updateAssistant((m) => ({
              ...m,
              toolEvents: m.toolEvents.map((te) =>
                te.call.tool_use_id === result.tool_use_id
                  ? { ...te, result }
                  : te,
              ),
            }));
            scrollToBottom();
          },
          onUsage: (usage) => {
            updateAssistant((m) => ({ ...m, usage }));
          },
          onError: (code, message) => {
            updateAssistant((m) => ({ ...m, error: `${code}: ${message}` }));
            setError(`${code}: ${message}`);
          },
          onDone: () => {
            updateAssistant((m) => ({ ...m, streaming: false }));
            setStreaming(false);
            cancelRef.current = null;
            scrollToBottom();
            // Sync sidebar so the new thread's LLM title + counts appear.
            refreshThreads();
          },
        },
      );
    },
    [client, streaming, scrollToBottom, threadId, refreshThreads],
  );

  const cancelStream = useCallback(() => {
    if (cancelRef.current) {
      cancelRef.current();
      cancelRef.current = null;
    }
    setStreaming(false);
  }, []);

  const newChat = useCallback(() => {
    if (streaming) return;
    setThreadId(null);
    setMessages([]);
    setError(null);
    setInput("");
  }, [streaming]);

  const selectThread = useCallback(
    async (id: string) => {
      if (streaming || id === threadId) return;
      setLoadingThread(true);
      setError(null);
      try {
        const t = await client.getThread(id);
        setThreadId(t.id);
        // Resumed turns are text only — no tool-step cards or citations.
        setMessages(
          t.messages.map((m, i) => ({
            id: `${m.role}_${i}_${m.timestamp}`,
            role: m.role,
            text: m.content,
            toolEvents: [],
            streaming: false,
          })),
        );
        scrollToBottom();
      } catch (e) {
        setError(e instanceof Error ? e.message : "Failed to load conversation");
      } finally {
        setLoadingThread(false);
      }
    },
    [client, streaming, threadId, scrollToBottom],
  );

  const removeThread = useCallback(
    async (id: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (streaming) return;
      try {
        await client.deleteThread(id);
      } catch {
        /* best-effort; a later refresh reconciles */
      }
      setThreads((prev) => prev.filter((t) => t.id !== id));
      if (id === threadId) {
        setThreadId(null);
        setMessages([]);
      }
    },
    [client, streaming, threadId],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        submit(input);
      }
    },
    [input, submit],
  );

  const hasMessages = messages.length > 0;

  return (
    <div className={styles.assistantLayout}>
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <Button
            className={styles.newChatButton}
            appearance="subtle"
            icon={<Add20Regular />}
            onClick={newChat}
            disabled={streaming}
          >
            New chat
          </Button>
        </div>
        {threads.length === 0 ? (
          <div className={styles.sidebarEmpty}>No conversations yet</div>
        ) : (
          <div className={styles.threadList}>
            {threads.map((t) => (
              <div
                key={t.id}
                className={mergeClasses(
                  styles.threadItem,
                  t.id === threadId && styles.threadItemActive,
                )}
                onClick={() => selectThread(t.id)}
                title={t.title}
              >
                <ChatMultiple20Regular />
                <div className={styles.threadItemText}>
                  <span className={styles.threadTitle}>{t.title}</span>
                  {t.preview && (
                    <span className={styles.threadPreview}>{t.preview}</span>
                  )}
                </div>
                <span className={mergeClasses("thread-delete", styles.threadDelete)}>
                  <Button
                    appearance="subtle"
                    size="small"
                    icon={<Delete16Regular />}
                    aria-label="Delete conversation"
                    onClick={(e) => removeThread(t.id, e)}
                    disabled={streaming}
                  />
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.chatPane}>
      {error && (
        <div className={styles.errorBar}>
          <MessageBar intent="error">
            <MessageBarBody>
              <MessageBarTitle>Couldn't reach Support Insights</MessageBarTitle>
              {error}
            </MessageBarBody>
          </MessageBar>
        </div>
      )}

      <div ref={scrollRef} className={styles.messages}>
        {loadingThread && (
          <div className={styles.emptyState}>
            <Spinner size="medium" label="Loading conversation…" />
          </div>
        )}
        {!loadingThread && !hasMessages && (
          <div className={styles.emptyState}>
            <Headset48Regular />
            <Text size={500}>Ask anything about LensLock's support history.</Text>
            <Text size={300}>
              Answers are grounded in real tickets from the last 12 months.
              Personal details are redacted before any data leaves to the LLM.
            </Text>
            <div className={styles.emptySamples}>
              {SAMPLE_QUESTIONS.map((q) => (
                <Button
                  key={q}
                  className={styles.sampleButton}
                  appearance="subtle"
                  onClick={() => submit(q)}
                  disabled={streaming}
                >
                  {q}
                </Button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m) =>
          m.role === "user" ? (
            <div key={m.id} className={styles.userBubble}>
              {m.text}
            </div>
          ) : (
            <AssistantBubble key={m.id} message={m} />
          ),
        )}
      </div>

      <div className={styles.inputRow}>
        <div className={styles.textarea}>
          <Textarea
            value={input}
            onChange={(_, data) => setInput(data.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask a question about LensLock support history…"
            resize="vertical"
            disabled={streaming}
            rows={2}
            style={{ width: "100%" }}
          />
        </div>
        <VoiceTextInputButton
          value={input}
          onChange={setInput}
          onDictationComplete={(text) => submit(text)}
          disabled={streaming}
        />
        {streaming ? (
          <Button
            icon={<Dismiss24Regular />}
            onClick={cancelStream}
            appearance="secondary"
          >
            Stop
          </Button>
        ) : (
          <Button
            icon={<Send24Regular />}
            appearance="primary"
            onClick={() => submit(input)}
            disabled={!input.trim()}
          >
            Send
          </Button>
        )}
      </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// AssistantBubble — renders one assistant message with citations + streaming spinner.
// ---------------------------------------------------------------------------
const TOOL_ICONS: Record<string, React.ReactNode> = {
  query_tickets_sql: <DatabaseSearch20Regular />,
  semantic_retrieve: <Search20Regular />,
  get_ticket_body: <Document20Regular />,
};

const ToolCallCard: React.FC<{ event: ToolEvent }> = ({ event }) => {
  const styles = useStyles();
  const [expanded, setExpanded] = useState(false);
  const { call, result } = event;
  const isPending = !result;
  const isError = !!result && !result.summary.ok;

  // Compact one-line header per tool name
  const inputHint = useMemo(() => {
    if (call.name === "query_tickets_sql") {
      const sql = (call.input.sql as string) || "";
      return `${call.input.container} · ${sql.slice(0, 90)}${sql.length > 90 ? "…" : ""}`;
    }
    if (call.name === "semantic_retrieve") {
      const q = (call.input.query as string) || "";
      const filters = call.input.filters as Record<string, unknown> | undefined;
      const fStr = filters && Object.keys(filters).length > 0 ? ` [${Object.entries(filters).map(([k, v]) => `${k}=${String(v)}`).join(", ")}]` : "";
      return `"${q}" k=${call.input.k ?? 8}${fStr}`;
    }
    if (call.name === "get_ticket_body") {
      return `#${call.input.ticket_id}`;
    }
    return JSON.stringify(call.input).slice(0, 100);
  }, [call]);

  const resultHint = useMemo(() => {
    if (!result) return null;
    const s = result.summary;
    if (!s.ok) return s.error ? `error: ${s.error}` : "error";
    if (call.name === "query_tickets_sql") {
      return `${s.row_count} row${s.row_count === 1 ? "" : "s"}${s.truncated ? " (truncated)" : ""}`;
    }
    if (call.name === "semantic_retrieve") return `${s.hit_count} hits`;
    if (call.name === "get_ticket_body") return `${s.body_chars} chars`;
    return "ok";
  }, [call, result]);

  return (
    <div className={styles.toolCard} onClick={() => setExpanded((e) => !e)}>
      <div className={styles.toolRow}>
        {TOOL_ICONS[call.name] || <DatabaseSearch20Regular />}
        <Text className={styles.toolName}>{call.name}</Text>
        <Text size={100} style={{ color: "inherit" }}>{inputHint}</Text>
        <span className={styles.toolSummary}>
          {isPending ? (
            <Spinner size="tiny" />
          ) : isError ? (
            <span className={styles.toolError}>
              <ErrorCircle20Filled /> {resultHint}
            </span>
          ) : (
            <span className={styles.toolOk}>
              <CheckmarkCircle20Filled /> {resultHint}
            </span>
          )}
        </span>
      </div>
      {expanded && (
        <div className={styles.toolDetail}>
          input: {JSON.stringify(call.input, null, 2)}
          {result && (
            <>
              {"\n\nresult: "}
              {JSON.stringify(result.summary, null, 2)}
            </>
          )}
        </div>
      )}
    </div>
  );
};

const AssistantBubble: React.FC<{ message: AssistantMessage }> = ({ message }) => {
  const styles = useStyles();
  const hasCitations = message.citations && message.citations.length > 0;
  const hasTools = message.toolEvents.length > 0;
  const usageHint = useMemo(() => {
    if (!message.usage) return null;
    const cached = message.usage.cache_read_input_tokens || 0;
    const created = message.usage.cache_creation_input_tokens || 0;
    const total = message.usage.input_tokens + created + cached;
    const rounds = message.usage.rounds ? ` · ${message.usage.rounds} round${message.usage.rounds === 1 ? "" : "s"}` : "";
    return `${total} in / ${message.usage.output_tokens} out${cached ? ` (cache hit: ${cached})` : ""}${rounds}`;
  }, [message.usage]);

  return (
    <div className={styles.assistantBlock}>
      {hasTools && (
        <div>
          <div className={styles.toolHeader}>
            agent steps
          </div>
          <div className={styles.toolList}>
            {message.toolEvents.map((te) => (
              <ToolCallCard key={te.call.tool_use_id} event={te} />
            ))}
          </div>
        </div>
      )}
      {hasCitations && (
        <div>
          <div className={styles.citationsHeader}>
            {message.citations!.length} relevant ticket
            {message.citations!.length === 1 ? "" : "s"}
          </div>
          <div className={styles.citationGrid}>
            {message.citations!.map((c) => (
              <Card key={c.ticket_id} className={styles.citationCard}>
                <CardHeader
                  image={<DocumentText24Regular />}
                  header={
                    <Text className={styles.citationSubject}>
                      #{c.ticket_id} {c.subject}
                    </Text>
                  }
                />
                <div className={styles.citationMeta}>
                  {c.type && <Badge appearance="outline" size="small">{c.type}</Badge>}
                  {c.product_line && (
                    <Badge appearance="outline" size="small" color="brand">
                      {c.product_line}
                    </Badge>
                  )}
                  {c.created_at && (
                    <Text size={100}>{c.created_at.slice(0, 10)}</Text>
                  )}
                  <Text size={100}>score {c.score.toFixed(2)}</Text>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}
      <div className={styles.assistantBubble}>
        {message.text ? (
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.text}</ReactMarkdown>
        ) : message.streaming ? (
          <Spinner size="tiny" label="Thinking…" />
        ) : null}
      </div>
      {message.error && (
        <MessageBar intent="warning">
          <MessageBarBody>{message.error}</MessageBarBody>
        </MessageBar>
      )}
      {usageHint && <div className={styles.usageRow}>{usageHint}</div>}
    </div>
  );
};

// ---------------------------------------------------------------------------
// InsightsTab — placeholder until trend detector lands.
// ---------------------------------------------------------------------------
const InsightsTab: React.FC = () => {
  const styles = useStyles();
  return (
    <div className={styles.insightsPlaceholder}>
      <DataTrending24Regular style={{ width: 48, height: 48 }} />
      <Text size={500}>Spike and anomaly detection — coming soon</Text>
      <Text size={300} style={{ maxWidth: 560 }}>
        Once the scheduled trend detector lands, this tab will surface topics
        rising or falling vs. their baseline, with drill-down into the tickets
        driving each shift.
      </Text>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------
export const SupportInsights: React.FC = () => {
  const styles = useStyles();
  const [selectedTab, setSelectedTab] = useState<TabValue>("assistant");
  const onTabSelect = useCallback((_: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value);
  }, []);

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <TabList selectedValue={selectedTab} onTabSelect={onTabSelect}>
          <Tab value="assistant" icon={<Chat24Regular />}>
            Assistant
          </Tab>
          <Tab value="insights" icon={<DataTrending24Regular />}>
            Insights
          </Tab>
        </TabList>
      </div>
      {selectedTab === "assistant" ? <AssistantTab /> : <InsightsTab />}
    </div>
  );
};

export default SupportInsights;
